/**
 * Утилиты для работы с debounce
 */

/**
 * Создает debounced функцию, которая откладывает выполнение до тех пор, 
 * пока не пройдет указанное количество миллисекунд с момента последнего вызова
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Создает debounced функцию с возможностью отмены
 */
export function createDebouncedFunction<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): {
  debouncedFunc: (...args: Parameters<T>) => void;
  cancel: () => void;
} {
  let timeout: NodeJS.Timeout | null = null;

  const debouncedFunc = (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(() => {
      func(...args);
    }, wait);
  };

  const cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return { debouncedFunc, cancel };
}

/**
 * Хук для создания debounced функции в React компонентах
 */
export function useDebounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  deps: React.DependencyList = []
): (...args: Parameters<T>) => void {
  const { debouncedFunc, cancel } = React.useMemo(
    () => createDebouncedFunction(func, wait),
    deps
  );

  React.useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return debouncedFunc;
}

// Импорт React для типов
import React from 'react';
